import { t, getLanguage, formatDate } from "../core/i18n.js";
import { api, getSyncInfo } from "../services/storage.js";
import { esc } from "../core/model.js";
import { icon } from "./icons.js";

const statusLabel = (status) =>
  t({ open: "New", reviewed: "Reviewed", resolved: "Resolved" }[status]);

export function openReports() {
  const host = document.getElementById("modal");
  const { session, requiresLogin } = getSyncInfo();
  const user = !requiresLogin ? session?.user : null;
  const owner = !!user?.canReviewReports;
  let rows = [],
    next = null,
    loaded = false,
    loading = false;
  const reference = crypto.randomUUID();
  host.innerHTML = `<div class="modal report-modal"><div class="tool-modal-head"><div><span class="eyebrow">DAYX / ${t("Feedback")}</span><h2>${t(owner ? "Reports" : "Report an issue")}</h2></div><button class="iconbtn" data-close aria-label="${t("Close")}">${icon("close")}</button></div><p class="tool-modal-description">${t(owner ? "Review user reports and update their status." : "Found something that does not work? Tell us what happened. Your report goes directly to DayX.")}</p>${owner ? `<div class="report-tabs" role="tablist"><button role="tab" aria-selected="true" aria-controls="reportInbox" data-report-tab="inbox">${t("Inbox")}</button><button role="tab" aria-selected="false" aria-controls="reportCompose" data-report-tab="compose">${t("Write a report")}</button></div>` : ""}<section id="reportCompose" ${owner ? "hidden" : ""}><form id="reportForm">${user ? `<div class="report-sender">${icon("check")}<span>${esc(user.email)}</span></div>` : `<label for="reportEmail">${t("Your email")}</label><input class="field" id="reportEmail" type="email" autocomplete="email" maxlength="254" required placeholder="name@example.com">`}<label for="reportSubject">${t("Issue title")}</label><input class="field" id="reportSubject" maxlength="120" minlength="3" required placeholder="${t("What went wrong?")}"><label for="reportDescription">${t("What happened?")}</label><textarea class="field" id="reportDescription" rows="5" minlength="10" maxlength="4000" required placeholder="${t("Describe the steps, what you expected and what happened instead.")}"></textarea><div class="report-context">${icon("dashboard")}<span>${esc(location.pathname)}</span><small>${t("Page and screen size are included to help us investigate.")}</small></div><p class="form-error" data-report-error role="alert"></p><div class="modal-actions"><button class="btn" type="button" data-close>${t("Cancel")}</button><button class="btn black" type="submit">${t("Send report")}${icon("arrow")}</button></div></form><div class="report-success" hidden><span>${icon("check")}</span><h3>${t("Thank you. Your report is received.")}</h3><p>${t("We will review the details. Thank you for helping improve DayX.")}</p><small data-report-reference></small><button class="btn black" data-close>${t("Done")}</button></div></section>${owner ? `<section id="reportInbox" role="tabpanel"><div class="report-inbox-head"><span data-report-count></span><button type="button" class="btn" data-report-refresh>${icon("reset")}${t("Refresh")}</button></div><p class="form-error" data-inbox-error role="alert"></p><div class="report-inbox-list" aria-live="polite"></div><button class="btn report-load-more" type="button" data-report-more hidden>${t("Load more")}</button></section>` : ""}</div>`;
  host.classList.add("show");
  const form = host.querySelector("#reportForm");
  form.onsubmit = async (event) => {
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = t("Sending…");
    const error = form.querySelector("[data-report-error]");
    error.textContent = "";
    try {
      const result = await api("/api/reports", {
        method: "POST",
        data: {
          id: reference,
          email: user?.email || form.querySelector("#reportEmail").value,
          subject: form.querySelector("#reportSubject").value,
          description: form.querySelector("#reportDescription").value,
          page: location.pathname,
          language: getLanguage(),
          viewport: `${innerWidth} × ${innerHeight}`,
        },
      });
      if (!form.isConnected) return;
      form.hidden = true;
      const success = host.querySelector(".report-success");
      success.hidden = false;
      success.querySelector("[data-report-reference]").textContent =
        `${t("Reference")} · ${result.id.slice(0, 8)}`;
      success.querySelector("button").focus();
      loaded = false;
    } catch (failure) {
      if (form.isConnected)
        error.textContent = failure.status
          ? failure.message
          : t(
              "Could not send the report. Check your connection and try again.",
            );
    } finally {
      button.disabled = false;
      button.innerHTML = `${t("Send report")}${icon("arrow")}`;
    }
  };
  if (!owner) return;
  const inbox = host.querySelector("#reportInbox");
  const list = inbox.querySelector(".report-inbox-list");
  const count = inbox.querySelector("[data-report-count]");
  const error = inbox.querySelector("[data-inbox-error]");
  const more = inbox.querySelector("[data-report-more]");
  function draw() {
    list.innerHTML = rows.length
      ? rows
          .map(
            (row) =>
              `<article class="report-inbox-item" data-report-id="${row.id}"><div class="report-item-top"><span class="report-status" data-status="${row.status}">${statusLabel(row.status)}</span><time>${formatDate(new Date(row.createdAt), { day: "numeric", month: "short", year: "numeric" })}</time></div><h3>${esc(row.subject)}</h3><p class="report-message">${esc(row.description)}</p><div class="report-item-context"><span>${esc(row.email)}</span><span>${esc(row.page)} · ${esc(row.viewport)} · ${esc(row.language.toUpperCase())}</span></div><label class="report-status-control">${t("Status")}<select class="field" data-report-status>${["open", "reviewed", "resolved"].map((status) => `<option value="${status}" ${status === row.status ? "selected" : ""}>${statusLabel(status)}</option>`).join("")}</select></label></article>`,
          )
          .join("")
      : `<div class="report-empty">${icon("check")}<strong>${t("No reports yet")}</strong><p>${t("Reports from your users will appear here.")}</p></div>`;
    more.hidden = !next;
  }
  async function load(append = false) {
    if (loading) return;
    loading = true;
    error.textContent = "";
    inbox.setAttribute("aria-busy", "true");
    inbox.querySelector("[data-report-refresh]").disabled = true;
    more.disabled = true;
    if (!loaded) count.textContent = t("Loading reports…");
    try {
      const result = await api(
        "/api/reports" +
          (append && next ? `?before=${encodeURIComponent(next)}` : ""),
      );
      if (!inbox.isConnected) return;
      rows = append ? [...rows, ...result.reports] : result.reports;
      next = result.next;
      loaded = true;
      count.textContent = t("{n} new reports", { n: result.open });
      count.dataset.open = String(result.open);
      draw();
    } catch (failure) {
      error.textContent = failure.status
        ? failure.message
        : t("Could not load reports. Try again.");
      if (!loaded) count.textContent = "";
    } finally {
      loading = false;
      inbox.removeAttribute("aria-busy");
      inbox.querySelector("[data-report-refresh]").disabled = false;
      more.disabled = false;
    }
  }
  host.querySelectorAll("[data-report-tab]").forEach((button) => {
    button.onclick = () => {
      const active = button.dataset.reportTab;
      host
        .querySelectorAll("[data-report-tab]")
        .forEach((tab) =>
          tab.setAttribute("aria-selected", String(tab === button)),
        );
      host.querySelector("#reportCompose").hidden = active !== "compose";
      inbox.hidden = active !== "inbox";
      if (active === "inbox" && !loaded) load();
      if (active === "compose") form.querySelector("input").focus();
    };
    button.onkeydown = (event) => {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault();
      const other = [...host.querySelectorAll("[data-report-tab]")].find(
        (tab) => tab !== button,
      );
      other.click();
      other.focus();
    };
  });
  inbox.querySelector("[data-report-refresh]").onclick = () => load();
  more.onclick = () => load(true);
  list.onchange = async (event) => {
    const select = event.target.closest("[data-report-status]");
    if (!select) return;
    const row = rows.find(
      (row) => row.id === select.closest("[data-report-id]").dataset.reportId,
    );
    const previousStatus = row.status;
    select.disabled = true;
    error.textContent = "";
    try {
      const result = await api(`/api/reports/${row.id}`, {
        method: "PATCH",
        data: { status: select.value },
      });
      Object.assign(row, result.report);
      const badge = select.closest("article").querySelector(".report-status");
      badge.textContent = statusLabel(row.status);
      badge.dataset.status = row.status;
      const previousCount = Number(count.dataset.open || 0);
      count.dataset.open = String(
        previousCount +
          (row.status === "open" ? 1 : 0) -
          (previousStatus === "open" ? 1 : 0),
      );
      count.textContent = t("{n} new reports", { n: count.dataset.open });
    } catch (failure) {
      select.value = row.status;
      error.textContent = failure.status
        ? failure.message
        : t("Could not update this report. Try again.");
    } finally {
      select.disabled = false;
    }
  };
  load();
}

export function bindReports() {
  const button = document.getElementById("reportBtn");
  button.innerHTML = `${icon("report")}<span>${t("Report")}</span>${icon("arrow")}`;
  button.setAttribute("aria-label", t("Report an issue"));
  button.onclick = openReports;
  window.addEventListener("dayx:language", () => {
    button.innerHTML = `${icon("report")}<span>${t("Report")}</span>${icon("arrow")}`;
    button.setAttribute("aria-label", t("Report an issue"));
  });
}
